/**
 * Developer API Keys Page
 * Allows developers to generate and manage API keys for accessing ParachPay APIs
 */
import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Key, 
  Plus, 
  Copy, 
  Eye, 
  EyeOff, 
  Trash2, 
  RefreshCw, 
  Shield,
  Clock,
  Activity,
  AlertTriangle,
  Check,
  Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABLE_SCOPES = [
  { id: 'read', label: 'Read', description: 'Read account data, balances, transactions' },
  { id: 'write', label: 'Write', description: 'Create and modify resources' },
  { id: 'transfer', label: 'Transfer', description: 'Send money and create transfers' },
  { id: 'exchange', label: 'Exchange', description: 'Currency exchange operations' },
  { id: 'cards', label: 'Cards', description: 'Virtual card management' },
  { id: 'analytics', label: 'Analytics', description: 'Access analytics and reports' }
];

const DeveloperKeys = () => {
  const [keys, setKeys] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyDialog, setNewKeyDialog] = useState({ open: false, key: null });
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: ['read'],
    expires_in_days: null,
    rate_limit: 1000
  });

  useEffect(() => {
    fetchKeys();
    fetchUsageStats();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/list`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setKeys(data);
      }
    } catch (error) {
      logger.error('Failed to fetch keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/usage-stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        setUsageStats(await res.json());
      }
    } catch (error) {
      logger.error('Failed to fetch usage stats:', error);
    }
  };

  const handleCreateKey = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setCreateDialogOpen(false);
        setNewKeyDialog({ open: true, key: data });
        fetchKeys();
        fetchUsageStats();
        // Reset form
        setFormData({
          name: '',
          description: '',
          scopes: ['read'],
          expires_in_days: null,
          rate_limit: 1000
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create API key');
      }
    } catch (error) {
      toast.error('Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleKey = async (keyId) => {
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/${keyId}/toggle`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchKeys();
      }
    } catch (error) {
      toast.error('Failed to toggle API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This cannot be undone.')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/${keyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('API key deleted');
        fetchKeys();
        fetchUsageStats();
      }
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!window.confirm('Regenerating will invalidate the current key immediately. Continue?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/developer-keys/${keyId}/regenerate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewKeyDialog({ open: true, key: data });
        fetchKeys();
      }
    } catch (error) {
      toast.error('Failed to regenerate API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleScope = (scopeId) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scopeId)
        ? prev.scopes.filter(s => s !== scopeId)
        : [...prev.scopes, scopeId]
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="developer-keys-page"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Key className="w-8 h-8 text-emerald-600" />
              API Keys
            </h1>
            <p className="text-muted-foreground">Generate keys for external API access</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl" data-testid="create-api-key-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">Create API Key</DialogTitle>
                <DialogDescription>
                  Generate a new API key for accessing ParachPay developer APIs
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Application"
                    className="rounded-xl"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this key used for?"
                    className="rounded-xl"
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Permissions</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_SCOPES.map(scope => (
                      <div 
                        key={scope.id}
                        className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.scopes.includes(scope.id) 
                            ? 'border-emerald-500 bg-emerald-500/5' 
                            : 'border-border hover:border-emerald-500/50'
                        }`}
                        onClick={() => toggleScope(scope.id)}
                      >
                        <Checkbox 
                          checked={formData.scopes.includes(scope.id)}
                          className="mt-0.5"
                        />
                        <div>
                          <p className="font-medium text-sm">{scope.label}</p>
                          <p className="text-xs text-muted-foreground">{scope.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expiration</Label>
                    <Select 
                      value={formData.expires_in_days?.toString() || 'never'}
                      onValueChange={(v) => setFormData({ 
                        ...formData, 
                        expires_in_days: v === 'never' ? null : parseInt(v) 
                      })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="never">Never expires</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="180">180 days</SelectItem>
                        <SelectItem value="365">1 year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Rate Limit (req/hour)</Label>
                    <Select 
                      value={formData.rate_limit.toString()}
                      onValueChange={(v) => setFormData({ ...formData, rate_limit: parseInt(v) })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="500">500</SelectItem>
                        <SelectItem value="1000">1,000</SelectItem>
                        <SelectItem value="5000">5,000</SelectItem>
                        <SelectItem value="10000">10,000</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateKey} disabled={creating} className="w-full rounded-xl">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                  Generate API Key
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {usageStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <Key className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Keys</p>
                    <p className="text-2xl font-bold">{usageStats.total_keys}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Shield className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Active Keys</p>
                    <p className="text-2xl font-bold">{usageStats.active_keys}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Activity className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total API Calls</p>
                    <p className="text-2xl font-bold">{usageStats.total_api_calls.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* API Keys List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Your API Keys</CardTitle>
            <CardDescription>Manage your API keys for external integrations</CardDescription>
          </CardHeader>
          <CardContent>
            {keys.length === 0 ? (
              <div className="text-center py-12">
                <Key className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No API keys created yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4 rounded-xl"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first key
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {keys.map((key) => (
                  <motion.div
                    key={key.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl border ${key.is_active ? 'bg-card' : 'bg-muted/50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium">{key.name}</h3>
                          <Badge variant={key.is_active ? 'default' : 'secondary'}>
                            {key.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                            {key.key_prefix}...
                          </code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={() => copyToClipboard(key.key_prefix)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        
                        {key.description && (
                          <p className="text-sm text-muted-foreground mb-2">{key.description}</p>
                        )}
                        
                        <div className="flex flex-wrap gap-1 mb-2">
                          {key.scopes.map(scope => (
                            <Badge key={scope} variant="outline" className="text-xs">
                              {scope}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Created {new Date(key.created_at).toLocaleDateString()}
                          </span>
                          {key.last_used_at && (
                            <span className="flex items-center gap-1">
                              <Activity className="w-3 h-3" />
                              Last used {new Date(key.last_used_at).toLocaleDateString()}
                            </span>
                          )}
                          <span>
                            {key.usage_count.toLocaleString()} calls
                          </span>
                          {key.expires_at && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <AlertTriangle className="w-3 h-3" />
                              Expires {new Date(key.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleToggleKey(key.id)}
                          title={key.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {key.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleRegenerateKey(key.id)}
                          title="Regenerate"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteKey(key.id)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Quick Start</CardTitle>
            <CardDescription>How to use your API key</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Authentication</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Include your API key in the <code className="bg-muted px-1 rounded">X-API-Key</code> header:
              </p>
              <pre className="bg-muted p-4 rounded-xl text-sm overflow-x-auto">
{`curl -X GET "${API_URL}/api/developer-keys/verify" \\
  -H "X-API-Key: ppk_your_api_key_here"`}
              </pre>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Verify Your Key</h4>
              <p className="text-sm text-muted-foreground">
                Test your API key using the <code className="bg-muted px-1 rounded">GET /api/developer-keys/verify</code> endpoint before making other requests.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* New Key Dialog */}
        <Dialog open={newKeyDialog.open} onOpenChange={(open) => setNewKeyDialog({ open, key: null })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                API Key Created
              </DialogTitle>
              <DialogDescription>
                Copy your API key now. You won't be able to see it again!
              </DialogDescription>
            </DialogHeader>
            {newKeyDialog.key && (
              <div className="space-y-4 py-4">
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Make sure to copy your API key now. You won't be able to see it again!
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Your API Key</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={newKeyDialog.key.api_key} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline"
                      onClick={() => copyToClipboard(newKeyDialog.key.api_key)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Name</p>
                    <p className="font-medium">{newKeyDialog.key.name}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Scopes</p>
                    <div className="flex flex-wrap gap-1">
                      {newKeyDialog.key.scopes.map(s => (
                        <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button 
                onClick={() => setNewKeyDialog({ open: false, key: null })} 
                className="w-full rounded-xl"
              >
                I've Saved My Key
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </Layout>
  );
};

export default DeveloperKeys;
